/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, Dispatch } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Button, Description, Input, Label, Sidebar } from "@nordcloud/gnui";
import { Fieldset, ContactsInput, LoaderWrap } from "~/components";
import { SUCCESS_TEXT } from "~/constants";
import { useAddEnvironment } from "~/hooks/graphql";
import { showSuccess } from "~/services/toast";
import { handleError, toInputError } from "~/tools/errors";
import { SwitchEnvType } from "~/views/applications/components/SwitchEnvType";
import { FormValues } from "./types";
import { EnvironmentSchema } from "./validators";

type Props = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<boolean>;
  applicationNid: string;
  applicationOwnerId: string;
};

export function AddEnvironmentSidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  applicationNid,
  applicationOwnerId,
}: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(EnvironmentSchema),
  });

  const [addEnvironment, { loading: loadingAdd }] = useAddEnvironment();

  const onSubmit = async (values: FormValues) => {
    try {
      const { contactId, name, type, description } = values;

      await addEnvironment({
        input: {
          applicationNid,
          ownerId: contactId || applicationOwnerId,
          displayName: name,
          name,
          type,
          description,
        },
      });

      setIsSidebarOpen(false);
      reset();
      showSuccess(SUCCESS_TEXT.environmentAdded);
    } catch (error) {
      handleError(toInputError(error));
    }
  };

  useEffect(() => {
    register("type", { required: true });
    register("contactId");
  }, [register]);

  const watcher = watch();

  return (
    <Sidebar
      title="Create New Environment"
      isOpen={isSidebarOpen}
      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
    >
      <LoaderWrap inContent loading={loadingAdd}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset>
            <Label required name="Environment Name" />
            <Input
              type="text"
              {...register("name", { required: true })}
              placeholder="Environment Name"
            />
            {errors.name && <Description>Name is required.</Description>}
          </Fieldset>
          <Fieldset>
            <Label required name="Select Environment Type" />
            <SwitchEnvType
              value={watcher.type}
              onChange={(newType) => {
                setValue("type", newType);
              }}
            />
            {errors.type && <Description>Type is required.</Description>}
          </Fieldset>
          <Fieldset>
            <Label name="Contact Person" />
            <ContactsInput
              placeholder="Contact Person"
              value={[watcher.contactId]}
              status={errors.contactId && "error"}
              onChange={(contact) => {
                setValue("contactId", contact);
              }}
            />
          </Fieldset>
          <Fieldset>
            <Label name="Environment Description" />
            <Input
              type="text"
              {...register("description")}
              placeholder="Environment Description"
            />
          </Fieldset>
          <Button type="submit">Create New Environment</Button>
        </form>
      </LoaderWrap>
    </Sidebar>
  );
}
